<template>
    <data-table
        v-model="selected"
        :class="$options.name"
        :headers="headers"
        :items="newAccounts"
        :loading="isProcessing"
        :show-select="showSelect && adminAuthority"
        :server-items-length="accountCount"
        :sort="sort"
        :must-sort="mustSort"
        :disable-sort="isDisabled || someProcessing || !mustSort"
        :disable-select="isDisabled || someProcessing"
        :not-found-message="$t('Account_not_found')"
        item-key="name"
        selectable-key="is_valid"
        @update:sort="$emit('update:sort', $event)"
    >
        <template v-slot:header.dummy-checkbox-col />
        <template v-slot:header.account_type_display="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.name_display="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.alias="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.randomize_type="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.connection_type="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.password_notification_selectable="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:header.immediate_fix_password_changeable="{ header }">
            <span :class="`${$options.name}__TableHeaderText`" v-text="$t(header.text)" />
        </template>
        <template v-slot:item.dummy-checkbox-col />
        <template v-slot:item.account_type_display="props">
            <span key="label"> {{ getAccountTypeText(props.item.account_type_display) }} </span>
        </template>
        <template v-slot:item.name_display="{ item }">
            <v-tooltip v-if="!displayItems.account_type_display && item.management_account" left>
                <template v-slot:activator="{ on }">
                    <v-icon small class="icon-padding-unified" v-on="on"> fa-user-tie </v-icon>
                </template>
                <span>{{ $t('Administrator_Account') }}</span>
            </v-tooltip>
            <span v-if="(item.id || accountDetailsPathName) && !someProcessing" @click.stop="">
                <router-link
                    key="link"
                    :to="`${accountDetailsPathName.replace(':account_id', item.id.toString())}`"
                    class="blue--text"
                >
                    {{ item.name_display || item.name }}
                </router-link>
            </span>
            <span v-else key="text">
                {{ item.name_display || item.name }}
            </span>
        </template>
        <template v-slot:item.alias="{ item }">
            {{ item.alias }}
        </template>
        <template v-slot:item.randomize_type="props">
            <template v-if="!isEditable">
                <v-tooltip left>
                    <template v-slot:activator="{ on }">
                        <v-icon small class="icon-padding-unified" v-on="on">
                            {{ getConnectionTypeIcon(props.item.connection_type) }}
                        </v-icon>
                    </template>
                    <span>
                        {{ getConnectionTypeMessage(props.item.connection_type) }}
                    </span>
                </v-tooltip>
                <span key="label"> {{ getRandomizeTypeText(props.item.randomize_type) }} </span>
            </template>
            <template v-else>
                <div v-if="isManagementAccount(props.item)" class="py-5 grey--text text--darken-1">
                    <label>{{ $t('Registered_by_the_administrator_account') }}</label>
                </div>
                <v-row v-else no-gutters>
                    <v-col>
                        <selector-randomize-type
                            ref="randomizeType"
                            v-model="props.item.randomize_type"
                            :disabled="
                                selected.some(v => v.name === props.item.name) || isProcessing || isTesting || isAdding
                            "
                            class="text-caption"
                            @click.native.stop
                        />
                    </v-col>
                </v-row>
            </template>
        </template>
        <template v-slot:item.connection_type="props">
            <div v-if="isManagementAccount(props.item)" class="py-5 grey--text text--darken-1">
                <label>{{ $t('Registered_by_the_administrator_account') }}</label>
            </div>
            <div v-else>
                <v-row no-gutters>
                    <v-col>
                        <password-mode-inputter
                            key="input"
                            :password-mode.sync="props.item.connection_type"
                            :password.sync="props.item.account_password"
                            :ssh-key.sync="props.item.ssh_key"
                            :ssh-key-passphrase.sync="props.item.ssh_key_passphrase"
                            :required="true"
                            :disabled="
                                selected.some(v => v.name === props.item.name) || isProcessing || isTesting || isAdding
                            "
                            :is-randomize-type-comprehensive="isRandomizeTypeComprehensive(props.item.randomize_type)"
                            :is-ssh-key-selectable="sshKeySelectable"
                            :is-used-ssh-key="isUsedSshKey"
                            @change-valid="props.item.is_valid = $event"
                            @click.native.stop
                        />
                    </v-col>
                </v-row>
                <v-row no-gutters>
                    <v-col v-show="!isRandomizeTypeComprehensive(props.item.randomize_type)" cols="3">
                        <button-app-control
                            :label="$t('Certification_Test')"
                            :icon="'network_check'"
                            :color="'primary'"
                            :disabled="
                                !props.item.is_valid ||
                                selected.some(v => v.name === props.item.name) ||
                                (clickedConnectionIndex !== props.index && isTesting) ||
                                isAdding ||
                                isProcessing
                            "
                            :loading="clickedConnectionIndex === props.index && isTesting"
                            @click="connectionTest(props)"
                            @click.native.stop
                        />
                    </v-col>
                </v-row>
            </div>
        </template>
        <template v-slot:item.password_notification_selectable="{ item }">
            <v-row v-if="isEditable" no-gutters>
                <v-spacer />
                <v-col cols="1" class="my-2">
                    <v-simple-checkbox
                        v-model="item.password_notification_selectable"
                        :disabled="selected.some(v => v.name === item.name) || isDisabled || someProcessing"
                        color="accent"
                    />
                </v-col>
                <v-spacer />
            </v-row>
            <v-row v-else no-gutters>
                <v-col>
                    <span v-if="item.password_notification_selectable">
                        {{ $t('Allow') }}
                    </span>
                    <span v-else-if="item.password_notification_selectable === false">
                        {{ $t('Not_allowed') }}
                    </span>
                    <span v-else></span>
                </v-col>
            </v-row>
        </template>
        <template v-slot:item.immediate_fix_password_changeable="{ item }">
            <div v-if="isEditable">
                <v-row
                    v-if="isManagementAccount(item) || isRandomizeTypeComprehensive(item.randomize_type)"
                    no-gutters
                ></v-row>
                <v-row v-else no-gutters>
                    <v-spacer />
                    <v-col cols="1" class="my-2">
                        <v-simple-checkbox
                            v-model="item.immediate_fix_password_changeable"
                            :disabled="selected.some(v => v.name === item.name) || isDisabled || someProcessing"
                            color="accent"
                        />
                    </v-col>
                    <v-spacer />
                </v-row>
            </div>
            <v-row v-else no-gutters>
                <v-col>
                    <span> {{ item.immediate_fix_password_changeable ? $t('Allow') : $t('Not_allowed') }} </span>
                </v-col>
            </v-row>
        </template>
    </data-table>
</template>

<script lang="ts">
import { Prop, Watch, Component, Vue } from 'vue-property-decorator';
import type { Account, NewAccount, TestAccount, AccountSortColumn } from '../../../store/modules/account/types';
import { AppContext } from '../../../store/context';
const RandomizeTypeList = AppContext.RandomizeTypeList;
const ConnectionTypeList = AppContext.ConnectionTypeList;
const AccountTypeRandomizableAccount = AppContext.AccountTypeRandomizableAccountList;
import PasswordModeInputter from '../general/PasswordModeInputter.vue';
import { ButtonAppControl, DataTable, SelectorRandomizeType } from '../../atoms';
import DataTableColumnWidthCalcurator from '../../../utils/types/DataTableColumnWidthCalcurator';
import SortParam from '../../../utils/types/SortParam';

@Component({
    name: 'AccountTable',
    components: {
        PasswordModeInputter,
        ButtonAppControl,
        DataTable,
        SelectorRandomizeType,
    },
})
export default class AccountTable extends Vue {
    @Prop({ default: () => [] }) accounts: Array<Account>;
    @Prop({ default: -1 }) accountCount: number;
    @Prop({ default: false }) processing: boolean;
    @Prop({ default: false }) testing: boolean;
    @Prop({ default: false }) adding: boolean;
    @Prop({ default: false }) fetchSuccess: boolean;
    @Prop({ default: false }) disabled: boolean;
    @Prop({ default: false }) mustSort: boolean;
    @Prop({ default: false }) editable: boolean;
    @Prop({ default: false }) adminAuthority: boolean;
    @Prop({ default: true }) showSelect: boolean;
    @Prop({ default: false }) usedSshKey: boolean;
    @Prop({ default: false }) isSshKeySelectable: boolean;
    @Prop({ default: '' }) accountDetailsPath: string;
    @Prop({ default: '' }) managementAccountName: string;
    @Prop({ default: '' }) domainName: string;
    @Prop({ default: () => new SortParam<AccountSortColumn>() }) sort: SortParam<AccountSortColumn>;
    @Prop({
        default: () => ({
            account_type_display: false,
            name_display: true,
            alias: true,
            randomize_type: true,
            connection_type: false,
            password_notification_selectable: false,
        }),
    })
    displayItems: any;
    @Prop({ default: true }) isAccountNameCaseSensitive: boolean;
    @Watch('selected', { deep: true })
    onSelectedChanged(accounts: Array<NewAccount>) {
        this.$emit('selected-accounts', accounts);
    }
    @Watch('newAccounts', { deep: true })
    keepInputContents() {
        this.$emit('keep-contents', this.newAccounts);
    }

    // widthRatioはチェックボックスの分を差し引いた 1.00 - 0.05 = 0.95 の値をそれぞれ分割して割り当てること
    get headers(): any[] {
        const result = [];

        const checkboxColWidth = 0.05;
        const baseWidth = {
            account_type_display: 0.15,
            name_display: 0.15,
            alias: 0.15,
            randomize_type: this.isNotificationSelectableDisplay
                ? 0.15 + (this.isEditable ? 0.1 : 0)
                : 0.55 + (this.isEditable ? 0.2 : 0),
            connection_type: 0.3,
            password_notification_selectable: this.isNotificationSelectableDisplay ? 0.15 : 0,
            immediate_fix_password_changeable: this.isRandomizationSelectableVisible ? 0.15 : 0,
        };
        const widthCalc = new DataTableColumnWidthCalcurator(checkboxColWidth, this.displayItems, baseWidth);

        if (this.showSelect) {
            if (this.adminAuthority) {
                result.push({
                    value: 'data-table-select', // チェックボックス列
                    width: widthCalc.selectColWidth(),
                });
            } else {
                result.push({
                    value: 'dummy-checkbox-col',
                    width: widthCalc.selectColWidth(),
                    sortable: false,
                });
            }
        }
        if (this.displayItems.account_type_display) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('account_type_display'),
                value: 'account_type_display',
                text: (this as any).$t('Account_Type'),
            });
        }
        if (this.displayItems.name_display) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('name_display'),
                value: 'name_display',
                text: (this as any).$t('Account_ID'),
            });
        }
        if (this.displayItems.alias) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('alias'),
                value: 'alias',
                text: (this as any).$t('Alias_Name'),
            });
        }
        if (this.displayItems.randomize_type) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('randomize_type'),
                value: 'randomize_type',
                text: (this as any).$t('Randomization_Method'),
            });
        }
        if (this.displayItems.connection_type) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('connection_type'),
                value: 'connection_type',
                text: (this as any).$t('Authenticate_Method'),
            });
        }
        if (this.displayItems.password_notification_selectable) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('password_notification_selectable'),
                value: 'password_notification_selectable',
                text: (this as any).$t('Allow_Password_Notification'),
            });
        }
        if (this.displayItems.immediate_fix_password_changeable) {
            result.push({
                align: 'left',
                width: widthCalc.calcColWidthPercentage('immediate_fix_password_changeable'),
                value: 'immediate_fix_password_changeable',
                text: (this as any).$t('Allow_Immediate_Password_Randomization'),
            });
        }

        return result;
    }

    selected = [];
    newAccountList = [] as Array<NewAccount>;
    clickedConnectionIndex = -1;

    /**
     * lifecycle hooks
     */
    created() {
        this.$watch(
            'accountList',
            (accounts: Array<Account>) => {
                const newAccounts = accounts.map(account => {
                    if (this.isEditable && this.isManagementAccount(account)) {
                        // 管理者アカウントと同じ場合は、ランダマイズ方式と認証方式はnull
                        account.randomize_type = '';
                        account.connection_type = '';
                        account.account_password = '';
                        account.password_notification_selectable = false;
                        account.immediate_fix_password_changeable = false;
                    } else {
                        if (!account.randomize_type) account.randomize_type = 'comprehensive'; // ランダマイズ方式設定がなければ初期値を設定する
                        if (!account.connection_type) account.connection_type = 'password'; // 認証方式設定がなければ初期値を設定する
                        if (!account.account_password) account.account_password = '';
                        account.password_notification_selectable = account.password_notification_selectable ?? false;
                        account.immediate_fix_password_changeable = account.immediate_fix_password_changeable ?? false;
                    }
                    (account as any).is_valid = true;
                    return account;
                });
                this.newAccountList = JSON.parse(JSON.stringify(newAccounts));
            },
            { immediate: true, deep: true }
        );
    }

    /**
     * getter
     */
    get isProcessing(): boolean {
        return this.processing;
    }
    get isTesting(): boolean {
        return this.testing;
    }
    get isAdding(): boolean {
        return this.adding;
    }
    get isFetchSuccess(): boolean {
        return this.fetchSuccess;
    }
    get isDisabled(): boolean {
        return this.disabled;
    }
    get someProcessing(): boolean {
        return this.processing || this.testing || this.adding || this.disabled;
    }
    get isEditable(): boolean {
        return this.editable;
    }
    get isNotificationSelectableDisplay(): boolean {
        return this.displayItems.password_notification_selectable ?? false;
    }
    get isRandomizationSelectableVisible(): boolean {
        return this.displayItems.immediate_fix_password_changeable ?? false;
    }
    get newAccounts(): Array<NewAccount> {
        return this.newAccountList;
    }
    get accountList(): Array<Account> {
        return this.accounts;
    }
    get accountDetailsPathName(): string {
        return this.accountDetailsPath;
    }
    get isUsedSshKey(): boolean {
        return this.usedSshKey;
    }
    get sshKeySelectable(): boolean {
        return this.isSshKeySelectable;
    }

    /**
     * methods
     */
    isManagementAccount(account: Account): boolean {
        if (account.management_account) {
            return true;
        }
        if (this.isAccountNameCaseSensitive) {
            return (
                account.name === this.managementAccountName ||
                account.name === this.managementAccountName + '@' + this.domainName.toLowerCase()
            );
        } else {
            return (
                account.name.toLowerCase() === this.managementAccountName.toLowerCase() ||
                account.name.toLowerCase() ===
                    this.managementAccountName.toLowerCase() + '@' + this.domainName.toLowerCase()
            );
        }
    }
    isRandomizeTypeComprehensive(randomize_type: string): boolean {
        return randomize_type === 'comprehensive';
    }
    getAccountTypeText(account_type_display: string): string {
        let text: string;
        let type: { text_key: string; value: string };
        type = AccountTypeRandomizableAccount.find(type => type.value === account_type_display);
        text = (this as any).$t(type.text_key);
        return text;
    }
    getConnectionTypeIcon(connection_type: string): string {
        if (connection_type) {
            return (this as any).$t(ConnectionTypeList.find(mode => mode.value === connection_type).icon);
        }
        return '';
    }
    getConnectionTypeMessage(connection_type: string): string {
        if (connection_type) {
            return (this as any).$t(ConnectionTypeList.find(mode => mode.value === connection_type).text_key);
        }
        return '';
    }
    getRandomizeTypeText(randomize_type: string): string {
        if (randomize_type) {
            return (this as any).$t(RandomizeTypeList.find(mode => mode.value === randomize_type).text_key);
        }
        return '';
    }
    connectionTest(props: any): void {
        this.clickedConnectionIndex = props.index;
        const account = {} as TestAccount;
        account.account_name = props.item.name;
        account.randomize_type = props.item.randomize_type;
        account.connection_type = props.item.connection_type;
        account.account_password = props.item.account_password;
        account.ssh_key = props.item.ssh_key;
        account.ssh_key_passphrase = props.item.ssh_key_passphrase;
        this.$emit('connection-test', account);
    }
    public onClearSelected(): void {
        this.selected = [];
    }
}
</script>

<style lang="scss" scoped>
.AccountTable {
    &__TableHeaderText {
        font-weight: bolder;
        color: white;
    }
    &__OmitName {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
}
</style>
